import { levelRow, type Level } from "odyssey-shared";
import { barFill } from "../../ui/kit/bar.ts";
import type { HudProgress } from "./store.ts";
import { say } from "../../words.ts";

/**
 * The XP bar's view (P2.4-T3, ADR-083 decision 6; the design's `XpBar` in `ui_kits/surfaces/CombatHud.jsx`, card
 * `ui_kits/surfaces/cards/xp.html`): what the bar in `HUD_SLOTS.xp` and the readout over it show, from the player's own
 * `progress` and the page's level table, whose rules version `welcome` matched. The bar fills with the XP gained since
 * the level's threshold, out of what the next level asks; at the table's last level, the cap, it fills whole in brass.
 * The level and the rate are the bar's tooltip (`xp-rate.ts`, P2.4-T8). Pure, so it is tested without a page.
 */

/**
 * What the bar and its readout draw.
 */
export interface XpView {
  readonly level: number;
  readonly fill: number;
  readonly capped: boolean;
  readonly into: number;
  readonly span: number;
  readonly levelText: string;
  readonly valueText: string;
}

/**
 * The bar's view.
 *
 * @param levels The page's level table.
 * @param progress The player's level and XP total.
 * @param inner The width of the bar's inside in logical pixels.
 * @param toasted The level the level-up toast names while it shows (P2.4-T5), null for none: while it names the
 *   player's level the bar is drawn full, as the design's level-up state draws the level just completed, while its
 *   value stays the real one.
 * @returns The view.
 */
export function xpView(levels: readonly Level[], progress: HudProgress, inner: number, toasted: number | null = null): XpView {
  const last = levels[levels.length - 1] as Level;
  const capped = progress.level >= last.level;
  const row = levelRow(levels, progress.level);
  const next = capped ? row : levelRow(levels, progress.level + 1);
  const span = Math.max(0, next.xp - row.xp);
  const into = capped ? span : Math.min(span, Math.max(0, progress.xp - row.xp));
  return {
    level: progress.level,
    fill: capped || toasted === progress.level ? inner : barFill(into, span, inner),
    capped,
    into,
    span,
    levelText: `${say("hud.xpBar.level")} ${progress.level}`,
    valueText: capped ? `${say("hud.xpBar.xp")} ${progress.xp}` : `${say("hud.xpBar.xp")} ${into} / ${span}`,
  };
}

/**
 * The chat log's System line for a level reached, beside the level-up toast (P2.4-T4; draft words).
 *
 * @param level The level reached.
 * @returns The line.
 * @example
 * levelUpLine(3);
 * // "You reached level 3."
 */
export function levelUpLine(level: number): string {
  return say("hud.levelUp.chat", { level: String(level) });
}
